/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { KeyboardAvoidingView} from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { ModalFormScroll } from '../../shared/modals/ModalFormScroll'
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader'
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { modalExperience } from '../../shared/modals/modalExperience.styles'
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors'
import { typography } from '../../shared/theme/typography';
import { ticketChrome } from '../../shared/ticket/ticketChrome.styles';
import type { BranchAccess } from '../../shell/session/store/sessionTypes';
import { getStockByProductAndBranch } from '../../inventory/public-api';
import { PurchaseProductPicker } from './PurchaseProductPicker';

type TransferItem = {
  productId: string;
  productName: string;
  productSku: string | null;
  unitOfMeasure: string | null;
  quantity: string;
  /** Stock actual en la sucursal origen (null = sin registro / no resuelto). */
  stockQuantity: number | null;
};

const EMPTY_TRANSFER_ITEM: TransferItem = {
  productId: '',
  productName: '',
  productSku: null,
  unitOfMeasure: null,
  quantity: '',
  stockQuantity: null,
};

type Props = {
  visible: boolean;
  currentBranchId: string;
  branches: BranchAccess[];
  submitting: boolean;
  onConfirm: (params: {
    transferId: string;
    fromBranchId: string;
    toBranchId: string;
    transferredBy?: string | null;
    notes: string;
    items: Array<{
      productId: string;
      productName: string;
      productSku: string | null;
      quantity: number;
      unitOfMeasure: string | null;
    }>;
  }) => void;
  onClose: () => void;
};

export function StockTransferModal({ visible, currentBranchId, branches, submitting, onConfirm, onClose }: Props) {
  const { width, height } = useWindowDimensions();
  const isMobile = width < CONTENT_WIDE_BREAKPOINT;
  const formMaxHeight = Math.max(480, Math.round(height * 0.68));
  const deskMaxWidth = Math.min(1200, Math.max(720, width - 48));
  const backdropPadding = useModalBackdropPadding();
  const insets = useSafeAreaInsets();

  const [toBranchId, setToBranchId] = useState('');
  const [items, setItems] = useState<TransferItem[]>([{ ...EMPTY_TRANSFER_ITEM }]);
  const [notes, setNotes] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const { confirm: confirmDiscard, ConfirmModal: DiscardConfirmModal } = useOperationalConfirm();

  // BUG-4: Reset state cuando el modal se cierra (visible=false)
  useEffect(() => {
    if (!visible) {
      setToBranchId('');
      setItems([{ ...EMPTY_TRANSFER_ITEM }]);
      setNotes('');
      setActionError(null);
    }
  }, [visible]);

  const otherBranches = branches.filter((b) => b.id !== currentBranchId);

  const isDirty =
    toBranchId !== '' ||
    notes.trim() !== '' ||
    items.some((item) => item.productId.trim() !== '' || item.quantity.trim() !== '');

  const requestClose = () => {
    if (submitting) return;
    if (!isDirty) {
      handleClose();
      return;
    }
    void confirmDiscard({
      title: 'Descartar transferencia',
      message: 'Hay datos cargados sin guardar. Si cerrás, se pierden.',
      confirmLabel: 'Descartar',
      destructive: true,
      presentation: 'overlay',
    }).then((confirmed) => {
      if (confirmed) handleClose();
    });
  };

  function handleItemChange(index: number, field: keyof TransferItem, value: string) {
    setItems(items.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  }

  function addRow() {
    setItems([...items, { ...EMPTY_TRANSFER_ITEM }]);
  }

  function removeRow(index: number) {
    setItems(items.filter((_, i) => i !== index));
  }

  function handleConfirm() {
    if (!toBranchId) {
      setActionError('Seleccioná una sucursal destino.');
      return;
    }
    // Validar stock disponible antes de confirmar — nunca truncar en silencio.
    const overStock = items.filter(
      (item) =>
        item.productId.trim() !== '' &&
        item.stockQuantity != null &&
        (parseDecimalInput(item.quantity) ?? 0) > item.stockQuantity,
    );
    if (overStock.length > 0) {
      setActionError(
        overStock.length === 1
          ? `"${overStock[0].productName || 'Producto'}" supera el stock disponible (${overStock[0].stockQuantity}).`
          : 'Hay cantidades que superan el stock disponible. Revisá los items marcados.',
      );
      return;
    }
    const validItems = items
      .filter((item) => item.productId.trim() && (parseDecimalInput(item.quantity) ?? 0) > 0)
      .map((item) => ({
        productId: item.productId.trim(),
        productName: item.productName.trim() || 'Producto',
        productSku: item.productSku,
        quantity: parseDecimalInput(item.quantity) ?? 0,
        unitOfMeasure: item.unitOfMeasure,
      }));
    if (validItems.length === 0) {
      setActionError('Agregá al menos un producto con cantidad mayor a cero.');
      return;
    }
    setActionError(null);

    onConfirm({
      transferId: crypto.randomUUID(),
      fromBranchId: currentBranchId,
      toBranchId,
      transferredBy: null,
      notes: notes.trim(),
      items: validItems,
    });
  }

  function reset() {
    setToBranchId('');
    setItems([{ ...EMPTY_TRANSFER_ITEM }]);
    setNotes('');
  }

  function handleClose() {
    reset();
    onClose();
  }

  const content = (
    <>
      <ModalFormField label="Sucursal destino">
        <View style={styles.pickerWrap}>
          {otherBranches.length === 0 ? (
            <Text style={styles.noBranches}>No hay otras sucursales disponibles</Text>
          ) : (
            otherBranches.map((branch) => (
              <TouchableOpacity
                key={branch.id}
                style={[styles.branchOption, toBranchId === branch.id && styles.branchOptionSelected]}
                accessibilityRole="button"
                testID={`purchases.transfer.branch.${branch.id}`}
                onPress={() => setToBranchId(branch.id)}
              >
                <Text style={[styles.branchOptionText, toBranchId === branch.id && styles.branchOptionTextSelected]}>
                  {branch.name}
                </Text>
              </TouchableOpacity>
            ))
          )}
        </View>
      </ModalFormField>

      <Text style={styles.sectionLabel}>Items a transferir</Text>
      {items.map((item, index) => (
        <View key={index} style={styles.itemBlock}>
          <ModalFormField label="Producto">
            <PurchaseProductPicker
              branchId={currentBranchId}
              selected={
                item.productId
                  ? { id: item.productId, name: item.productName, sku: item.productSku }
                  : null
              }
              disabled={submitting}
              onSelect={(row) => {
                setItems((prev) =>
                  prev.map((it, i) =>
                    i === index
                      ? {
                          ...it,
                          productId: row.id,
                          productName: row.name,
                          productSku: row.sku,
                          unitOfMeasure: row.unitOfMeasure,
                          stockQuantity: null,
                        }
                      : it,
                  ),
                );
                // Stock actual en la sucursal origen para validar la cantidad.
                void getStockByProductAndBranch(currentBranchId, row.id)
                  .then((stock) => {
                    setItems((prev) =>
                      prev.map((it, i) =>
                        i === index && it.productId === row.id
                          ? { ...it, stockQuantity: stock?.quantity ?? 0 }
                          : it,
                      ),
                    );
                  })
                  .catch(() => {
                    /* sin stock → queda null, se avisa al confirmar */
                  });
              }}
              onClear={() => {
                setItems((prev) =>
                  prev.map((it, i) =>
                    i === index
                      ? {
                          ...it,
                          productId: '',
                          productName: '',
                          productSku: null,
                          unitOfMeasure: null,
                        }
                      : it,
                  ),
                );
              }}
            />
          </ModalFormField>
          <View style={styles.itemNumericRow}>
            <View style={styles.flex1}>
              <Text style={styles.inputLabel}>Cantidad</Text>
              <NumericTextInput
                kind="decimalMoney"
                value={item.quantity}
                onChangeText={(v: string) => handleItemChange(index, 'quantity', v)}
                placeholder="0"
                style={[
                  modalExperience.formInput,
                  item.productId.trim() !== '' &&
                  item.stockQuantity != null &&
                  (parseDecimalInput(item.quantity) ?? 0) > item.stockQuantity
                    ? styles.inputError
                    : undefined,
                ]}
                placeholderTextColor={ticketColors.ink3}
                testID={`purchases.transfer.qty.${index}`}
              />
              {item.productId.trim() !== '' ? (
                <Text
                  style={[
                    styles.stockHint,
                    item.stockQuantity != null &&
                    (parseDecimalInput(item.quantity) ?? 0) > item.stockQuantity
                      ? styles.stockHintError
                      : undefined,
                  ]}
                  testID={`purchases.transfer.stock.${index}`}
                >
                  {item.stockQuantity == null
                    ? 'Stock: consultando…'
                    : item.stockQuantity <= 0
                      ? 'Sin stock en esta sucursal'
                      : `Stock: ${item.stockQuantity}${item.unitOfMeasure ? ` ${item.unitOfMeasure}` : ''}`}
                </Text>
              ) : null}
            </View>
            {items.length > 1 ? (
              <TouchableOpacity style={styles.removeBtn} onPress={() => removeRow(index)}>
                <Text style={styles.removeBtnText}>×</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        </View>
      ))}
      <TouchableOpacity style={styles.addRowBtn} onPress={addRow}>
        <Text style={styles.addRowBtnText}>+ Agregar item</Text>
      </TouchableOpacity>

      <ModalFormField label="Notas (opcional)">
        <TextInput
          style={[modalExperience.formInput, modalExperience.formInputMultiline]}
          value={notes}
          onChangeText={setNotes}
          placeholder="Notas internas"
          placeholderTextColor={ticketColors.ink3}
          multiline
        />
      </ModalFormField>
      {actionError ? (
        <Text style={styles.actionError}>{actionError}</Text>
      ) : null}
    </>
  );

  if (isMobile) {
    return (
      <>
      <Modal visible={visible} animationType="slide" transparent onRequestClose={requestClose} accessibilityViewIsModal>
        <View style={ticketChrome.sheetBackdrop}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={requestClose}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <KeyboardAvoidingView behavior="padding" style={styles.keyboardWrap}>
            <View style={[ticketChrome.sheetCard, { maxHeight: height * 0.96, paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
              <View style={ticketChrome.grabber} />
              <View style={ticketChrome.sheetHeader}>
                <Text style={ticketChrome.sheetSubtitle}>Transferir stock</Text>
                <TouchableOpacity
                  onPress={requestClose}
                  style={ticketChrome.sheetCloseBtn}
                  accessibilityRole="button"
                  accessibilityLabel="Cerrar"
                >
                  <Text style={ticketChrome.sheetCloseGlyph}>×</Text>
                </TouchableOpacity>
              </View>
              <ModalAwareScrollView
                style={styles.sheetScroll}
                contentContainerStyle={styles.sheetScrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
              >
                {content}
              </ModalAwareScrollView>
              <View style={ticketChrome.foot}>
                <TouchableOpacity
                  style={[ticketChrome.btnGhost, { flex: 1, minWidth: 0 }]}
                  onPress={requestClose}
                  disabled={submitting}
                >
                  <Text style={ticketChrome.btnGhostText}>Cancelar</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[ticketChrome.btnGold, { flex: 1, minWidth: 0 }, submitting && styles.saveBtnDisabled]}
                  onPress={handleConfirm}
                  disabled={submitting || !toBranchId}
                  accessibilityRole="button"
                  accessibilityLabel="Confirmar transferencia"
                  testID="purchases.transfer.confirm"
                >
                  <Text style={[ticketChrome.btnGoldText, styles.confirmLabel]} numberOfLines={2}>
                    Confirmar transferencia
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>
      {DiscardConfirmModal}
      </>
    );
  }

  return (
    <>
    <Modal visible={visible} animationType="fade" transparent onRequestClose={requestClose} accessibilityViewIsModal>
      <View style={[posUi.modalBackdrop, modalExperience.modalRoot, backdropPadding]}>
        <View
          style={[
            posUi.modalCard,
            modalShellShared.modalCardShell,
            modalExperience.modalWidthWide,
            { gap: spacing.sm, width: '100%', maxWidth: deskMaxWidth, alignSelf: 'center', maxHeight: '92%' },
          ]}
        >
          <ModalScreenHeader title="Transferir stock entre sucursales" onClose={requestClose} />
          <ModalFormScroll maxHeight={formMaxHeight} contentContainerStyle={modalExperience.formPanel}>{content}</ModalFormScroll>
          <View style={[posUi.modalActions, modalExperience.footerActions, styles.footerWrap]}>
            <TouchableOpacity
              style={[posUi.secondaryButton, posUi.modalButtonHalf]}
              onPress={requestClose}
              disabled={submitting}
            >
              <Text style={posUi.secondaryButtonText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[posUi.primaryButton, posUi.modalButtonHalf, submitting && posUi.disabledCard]}
              onPress={handleConfirm}
              disabled={submitting || !toBranchId}
              accessibilityRole="button"
              accessibilityLabel="Confirmar transferencia"
              testID="purchases.transfer.confirm"
            >
              <Text style={[posUi.primaryButtonText, styles.confirmLabel]} numberOfLines={2}>
                Confirmar transferencia
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
    {DiscardConfirmModal}
    </>
  );
}

const styles = StyleSheet.create({
  keyboardWrap: {
    width: '100%',
    // Acota el KAV al backdrop: sin esto el maxHeight % del sheet no resuelve en web
    maxHeight: '100%',
    justifyContent: 'flex-end',
  },
  sheetScroll: {
    flexShrink: 1,
    minHeight: 0,
  },
  sheetScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: spacing.lg,
    gap: spacing.md,
  },
  footerWrap: {
    flexWrap: 'wrap',
  },
  confirmLabel: {
    textAlign: 'center',
    flexShrink: 1,
  },
  saveBtnDisabled: {
    opacity: 0.5,
  },
  pickerWrap: {
    gap: 8,
  },
  noBranches: {
    color: ticketColors.ink3,
    fontSize: 14,
    padding: 12,
  },
  branchOption: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  branchOptionSelected: {
    borderColor: ticketColors.brandPrimary,
    backgroundColor: ticketColors.paper2,
  },
  branchOptionText: {
    fontSize: 15,
    color: ticketColors.ink,
  },
  branchOptionTextSelected: {
    color: ticketColors.brandPrimary,
    fontWeight: '600',
  },
  sectionLabel: {
    ...typography.body,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  itemBlock: {
    gap: 8,
    marginBottom: 4,
  },
  itemNumericRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-end',
  },
  flex1: { flex: 1, minWidth: 80 },
  inputLabel: {
    fontSize: 12,
    color: ticketColors.ink3,
    marginBottom: 4,
  },
  removeBtn: {
    width: 32,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ticketColors.paper2,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.bad,
  },
  removeBtnText: {
    color: ticketColors.bad,
    fontSize: 18,
  },
  addRowBtn: {
    padding: 12,
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.brandPrimary,
  },
  addRowBtnText: {
    color: ticketColors.brandPrimary,
    fontWeight: '500',
  },
  inputError: {
    borderColor: ticketColors.bad,
  },
  stockHint: {
    fontSize: 12,
    color: ticketColors.ink3,
    marginTop: 4,
  },
  stockHintError: {
    color: ticketColors.bad,
    fontWeight: '600',
  },
  actionError: {
    color: ticketColors.bad,
    fontSize: 13,
    paddingVertical: 8,
  },
});
